/**
 * Spinner — Animated loading indicator for indeterminate waits.
 *
 * Spinner Component Guidelines
 * - Use Spinner for indeterminate loading when the content layout is unknown.
 *   Prefer Skeleton when the loaded layout is predictable (cards, lists, text).
 * - Use `variant="ai"` for AI-powered actions (generation, analysis) — it renders
 *   a branded gradient spinner that signals AI involvement.
 * - Default size is 16px (`size-4`). Override via `className` to match the context
 *   (e.g. `size-3` inside a small button, `size-8` for a full-section loader).
 * - Spinner includes `role="status"` and a default `aria-label="Loading"` — override
 *   `aria-label` when the context is more specific (e.g. "Generating response").
 * @see https://ui.shadcn.com/docs/components/spinner shadcn/ui Spinner documentation
 */
import type { VariantProps } from "class-variance-authority";

import React from "react";

import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";
import { Loader2Icon } from "lucide-react";

const spinnerVariants = cva("size-4 animate-spin", {
  variants: {
    variant: {
      primary: "text-primary",
      default: "text-default",
      rose: "text-rose",
      white: "text-white",
      ai: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type SpinnerVariants = VariantProps<typeof spinnerVariants>["variant"];

type SpinnerProps = Omit<React.ComponentProps<"svg">, "children"> &
  VariantProps<typeof spinnerVariants>;

function AIGradientSpinnerIcon(props: React.ComponentProps<"svg">) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <defs>
        <linearGradient
          id="ai-spinner-gradient"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
        >
          <stop offset="0%" stopColor="#FDD37E" />
          <stop offset="50%" stopColor="#F34D72" />
          <stop offset="100%" stopColor="#7C7FBA" />
        </linearGradient>
      </defs>
      <path
        d="M21 12a9 9 0 1 1-6.219-8.56"
        stroke="url(#ai-spinner-gradient)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Spinner({ className, variant = "default", ...props }: SpinnerProps) {
  // NOTE (Ben, 2026-01-22): Uses a custom SVG because gradients cannot be applied to Lucide icons
  if (variant === "ai") {
    return (
      <AIGradientSpinnerIcon
        role="status"
        aria-label={props["aria-label"] ?? "Loading"}
        className={cn(spinnerVariants({ variant }), className)}
        {...props}
      />
    );
  }

  return (
    <Loader2Icon
      role="status"
      aria-label={props["aria-label"] ?? "Loading"}
      className={cn(spinnerVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Spinner, type SpinnerVariants };
